
import { useCombobox, UseComboboxReturnValue } from 'downshift'
import * as React from 'react'
import { TComboboxOption } from '../types'

type TUseComboboxControls = {
    options: TComboboxOption[]
    defaultOption?: TComboboxOption
    onSelect: (option: TComboboxOption | null) => void
}

type TUseComboboxControlsReturn = {
    controls: UseComboboxReturnValue<TComboboxOption>
    inputValue: string
    innerOptions: TComboboxOption[]
    selectedOption: TComboboxOption | null
    setInputValue: React.Dispatch<React.SetStateAction<string>>
    setInnerOptions: React.Dispatch<React.SetStateAction<TComboboxOption[]>>
    setSelectedOption: React.Dispatch<React.SetStateAction<TComboboxOption | null>>
}

export const useComboboxControls = ({ options, defaultOption, onSelect }: TUseComboboxControls): TUseComboboxControlsReturn => {

    const [inputValue, setInputValue] = React.useState<string>('')
    const [innerOptions, setInnerOptions] = React.useState(options)
    const [selectedOption, setSelectedOption] = React.useState(
        defaultOption ?? null,
    )

    // Use a ref to track the current selected ID to avoid stale closure issues
    const selectedOptionIdRef = React.useRef<number | string | null>(selectedOption?.id ?? null)

    // Keep the ref in sync with the state
    React.useEffect(() => {
        // eslint-disable-next-line functional/immutable-data
        selectedOptionIdRef.current = selectedOption?.id ?? null
    }, [selectedOption?.id])

    const controls = useCombobox({
        defaultIsOpen: false,
        inputValue,
        itemToString: (item) => item?.label ?? '',
        items: innerOptions as TComboboxOption[],
        onSelectedItemChange: ({ selectedItem: selectedOptionOnSelectedItemChange }) => {
            // Only update if the selection actually changed to prevent infinite loops
            // Use ref to get the truly current value, not a stale closure
            if (selectedOptionOnSelectedItemChange && selectedOptionOnSelectedItemChange.id !== selectedOptionIdRef.current) {
                // eslint-disable-next-line functional/immutable-data
                selectedOptionIdRef.current = selectedOptionOnSelectedItemChange.id
                setSelectedOption(selectedOptionOnSelectedItemChange)
                setInputValue(selectedOptionOnSelectedItemChange.label)
                onSelect(selectedOptionOnSelectedItemChange)
            }
        },
        selectedItem: selectedOption,
    })

    React.useEffect(() => {
        if (defaultOption && defaultOption.id !== selectedOptionIdRef.current) {
            // eslint-disable-next-line functional/immutable-data
            selectedOptionIdRef.current = defaultOption.id
            setSelectedOption(defaultOption)
        }
    }, [defaultOption])

    const optionsRef = React.useRef(options)
    React.useEffect(() => {
        const changed = options.length !== optionsRef.current.length ||
            options.some((opt, i) => opt.id !== optionsRef.current[i]?.id)
        if (changed) {
            // eslint-disable-next-line functional/immutable-data
            optionsRef.current = options
            setInnerOptions(options)
        }
    }, [options])

    React.useEffect(() => {
        if (!controls.isOpen) {
            setInputValue(selectedOption?.label ?? '')
            setInnerOptions(options)
        }
    }, [controls.isOpen, options, selectedOption])

    return { controls, inputValue, setInputValue, innerOptions, setInnerOptions, selectedOption, setSelectedOption }
}
